import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, extractError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO_IMG = "https://images.pexels.com/photos/17001844/pexels-photo-17001844.jpeg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { user } = await login(identifier, password);
      toast.success("Welcome back");
      if (user.role === "super_admin") navigate("/super");
      else navigate("/app");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F9F8F6]">
      <div className="hidden lg:block relative">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/25 to-transparent" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Link to="/" className="font-serif text-3xl tracking-tight">
            <img 
              src="/planniq_logo_white.png" 
              alt="EventFlow" 
              className="h-10 w-auto"
            />
          </Link>
          <div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tighter max-w-lg leading-tight">
              Where Every Booking Becomes Better Business.
            </h2>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="px-6 sm:px-12 py-6 flex items-center justify-between">
          <Link to="/" className="lg:hidden font-serif text-2xl tracking-tight text-[#1A1A1A]"> 
            <img 
              src="/planniq_logo_dark.png" 
              alt="EventFlow" 
              className="h-10 w-auto"
            />
            </Link>

        </div>

        <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-8">
          <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="login-form">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#D85C38]">Welcome back</div>
              <h1 className="font-serif text-4xl md:text-5xl tracking-tighter mt-2">Sign in</h1>
            </div>

            <div>
              <Label htmlFor="identifier">Mobile or email</Label>
              <Input
                id="identifier"
                type="text"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="9876543210 or you@studio.com"
                className="mt-1.5 h-11"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex justify-between items-end">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-zinc-600 hover:text-[#D85C38]" data-testid="forgot-link">
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-full bg-[#D85C38] text-white hover:bg-[#D85C38]/90"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>

            <Button
              onClick={() => navigate('/signup')}
              className="w-full h-11 rounded-full bg-black text-white hover:bg-black/80"
              data-testid="login-to-signup-link"
            >
              Create an account
            </Button>

            <div className="text-xs text-zinc-500">
              By signing in you agree to our terms. Sessions last 24 hours.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
